import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

import {
  AccessTimeOutlined,
  DeleteOutline,
  ForumOutlined,
  MarkUnreadChatAltOutlined,
  PeopleAltOutlined,
  PendingActionsOutlined,
  ReportProblem,
  VisibilityOutlined,
} from "@mui/icons-material";

import {
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { format, isSameDay, parseISO } from "date-fns";
import { getBackendUrl } from "../../config";

const backendUrl = getBackendUrl();

const getIsDarkMode = (theme) =>
  theme.palette.type === "dark" || theme.palette.mode === "dark";

const useStyles = makeStyles((theme) => {
  const isDarkMode = getIsDarkMode(theme);

  return {
    root: {
      width: "100%",
      minHeight: "100vh", // Garante que o fundo cubra toda a altura da tela
      padding: theme.spacing(3), // Adiciona um espaçamento interno nas bordas da tela
      backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc", // Fundo cinza escuro para modo escuro, e cinza bem claro/suave para modo claro
    },
    summaryGrid: {
      marginBottom: theme.spacing(3),
    },
    summaryCard: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 20,
      padding: theme.spacing(2.25),
      minHeight: 132,
      border: `1px solid ${isDarkMode ? "rgba(148, 163, 184, 0.16)" : "rgba(15, 23, 42, 0.08)"}`,
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.88) 100%)"
        : "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.92) 100%)",
      boxShadow: isDarkMode
        ? "0 22px 45px rgba(2, 6, 23, 0.34)"
        : "0 18px 38px rgba(15, 23, 42, 0.10)",
    },
    summaryGlow: {
      position: "absolute",
      inset: "auto -40px -52px auto",
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: isDarkMode
        ? "radial-gradient(circle, rgba(59,130,246,0.26) 0%, rgba(59,130,246,0) 72%)"
        : "radial-gradient(circle, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0) 72%)",
      pointerEvents: "none",
    },
    summaryLabel: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1.5),
      fontWeight: 600,
      fontSize: 13,
      letterSpacing: 0.2,
    },
    summaryValue: {
      color: theme.palette.text.primary,
      fontWeight: 800,
      letterSpacing: -0.8,
      lineHeight: 1,
      marginBottom: theme.spacing(1),
    },
    summaryCaption: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
      maxWidth: 220,
    },
    board: {
      width: "100%",
    },
    columnWrap: {
      display: "flex",
    },
    column: {
      width: "100%",
      borderRadius: 24,
      overflow: "hidden",
      border: `1px solid ${isDarkMode ? "rgba(148, 163, 184, 0.16)" : "rgba(15, 23, 42, 0.08)"}`,
      background: isDarkMode
        ? "linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.86) 100%)"
        : "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
      boxShadow: isDarkMode
        ? "0 22px 45px rgba(2, 6, 23, 0.28)"
        : "0 18px 38px rgba(15, 23, 42, 0.10)",
      backdropFilter: "blur(12px)",
      display: "flex",
      flexDirection: "column",
      minHeight: 420,
    },
    columnHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(2.25),
      borderBottom: `1px solid ${isDarkMode ? "rgba(148, 163, 184, 0.10)" : "rgba(15, 23, 42, 0.07)"}`,
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(30,41,59,0.96) 0%, rgba(15,23,42,0.72) 100%)"
        : "linear-gradient(135deg, rgba(248,250,252,0.96) 0%, rgba(226,232,240,0.65) 100%)",
    },
    columnTitleWrap: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      minWidth: 0,
      flex: 1,
    },
    columnAvatar: {
      width: 50,
      height: 50,
      fontSize: 18,
      fontWeight: 700,
      border: `2px solid ${isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.8)"}`,
      boxShadow: isDarkMode
        ? "0 10px 24px rgba(15, 23, 42, 0.35)"
        : "0 12px 24px rgba(15, 23, 42, 0.14)",
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(59,130,246,0.75) 0%, rgba(14,165,233,0.8) 100%)"
        : "linear-gradient(135deg, rgba(79,70,229,0.90) 0%, rgba(14,165,233,0.92) 100%)",
    },
    pendingAvatar: {
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(245,158,11,0.95) 0%, rgba(249,115,22,0.95) 100%)"
        : "linear-gradient(135deg, rgba(251,191,36,0.98) 0%, rgba(249,115,22,0.95) 100%)",
    },
    columnTitle: {
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    columnSubtitle: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      marginTop: 2,
    },
    countChip: {
      fontWeight: 700,
      borderRadius: 999,
      border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.14)" : "rgba(79,70,229,0.12)"}`,
      backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(99,102,241,0.08)",
      color: theme.palette.text.primary,
    },
    ticketList: {
      padding: theme.spacing(1.25),
      display: "grid",
      gap: theme.spacing(1.25),
      // --- Novas propriedades para limitar a 5 tickets com scroll ---
      maxHeight: "calc((74px * 5) + (10px * 4))", // Altura aproximada para 5 itens + espaçamento
      overflowY: "auto",
      paddingRight: theme.spacing(0.75), // Espaço para a barra não cobrir o card
      ...theme.scrollbarStyles, // Aplica o estilo de scroll customizado do seu tema, se houver
    },
    ticketCard: {
      padding: theme.spacing(0.95), // Reduzido em ~20% (era 1.2) para encolher a altura
      width: "90%", // Reduz a largura em 10%
      marginLeft: "auto", // Centraliza o card na coluna, se preferir
      marginRight: "auto", // Centraliza o card na coluna, se preferir
      borderRadius: 14, // Suaviza os cantos para combinar com o tamanho menor
      border: `1px solid ${isDarkMode ? "rgba(148, 163, 184, 0.12)" : "rgba(15, 23, 42, 0.06)"}`,
      background: isDarkMode
        ? "linear-gradient(180deg, rgba(30,41,59,0.70) 0%, rgba(15,23,42,0.66) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)",
      boxShadow: isDarkMode
        ? "0 12px 30px rgba(2, 6, 23, 0.22)"
        : "0 12px 24px rgba(15, 23, 42, 0.06)",
      transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
      cursor: "pointer",
      '&:hover': {
        transform: "translateY(-2px)",
        boxShadow: isDarkMode
          ? "0 20px 36px rgba(2, 6, 23, 0.30)"
          : "0 18px 32px rgba(15, 23, 42, 0.12)",
        borderColor: isDarkMode ? "rgba(96,165,250,0.30)" : "rgba(79,70,229,0.20)",
      },
    },
    ticketTopRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
    },
    ticketMain: {
      flex: 1,
      minWidth: 0,
    },
    contactName: {
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    ticketSnippet: {
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
      minHeight: 20,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      wordBreak: "break-word",
    },
    contactAvatar: {
      width: 44,
      height: 44,
      fontWeight: 700,
      boxShadow: isDarkMode
        ? "0 10px 24px rgba(15, 23, 42, 0.35)"
        : "0 10px 20px rgba(15, 23, 42, 0.10)",
    },
    badgeRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    badge: {
      borderRadius: 999,
      padding: "5px 10px",
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: 0.2,
      color: "#fff",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.16)",
    },
    channelBadge: {
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(16,185,129,0.88) 0%, rgba(6,182,212,0.82) 100%)"
        : "linear-gradient(135deg, rgba(16,185,129,0.95) 0%, rgba(6,182,212,0.95) 100%)",
    },
    footerRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    timeWrap: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 600,
    },
    unreadTime: {
      color: theme.palette.primary.main,
    },
    unreadChip: {
      fontWeight: 700,
      color: theme.palette.primary.main,
      borderColor: `${theme.palette.primary.main}33`,
      backgroundColor: isDarkMode ? "rgba(96,165,250,0.12)" : "rgba(99,102,241,0.08)",
    },
    actionRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginLeft: theme.spacing(1),
      flexShrink: 0,
    },
    actionIcon: {
      cursor: "pointer",
      transition: "transform 0.15s ease, color 0.15s ease",
      color: theme.palette.text.secondary,
      '&:hover': {
        transform: "scale(1.08)",
        color: theme.palette.primary.main,
      },
    },
    deleteIcon: {
      color: theme.palette.error.main,
      '&:hover': {
        color: theme.palette.error.dark,
      },
    },
    emptyState: {
      padding: theme.spacing(5, 3),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      color: theme.palette.text.secondary,
      minHeight: 240,
      gap: theme.spacing(1.25),
    },
    emptyStateIcon: {
      fontSize: 34,
      color: isDarkMode ? "rgba(148,163,184,0.85)" : "rgba(71,85,105,0.65)",
    },
    dialogPaper: {
      borderRadius: 22,
      overflow: "hidden",
      background: isDarkMode
        ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%)",
    },
    dialogTitle: {
      padding: theme.spacing(2.25, 3, 1),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogSubtitle: {
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
    messageScroll: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.25),
      maxHeight: 500,
      overflowY: "auto",
      padding: theme.spacing(0.5, 0.5, 1),
      ...theme.scrollbarStyles,
    },
    messageRow: {
      display: "flex",
    },
    messageBubble: {
      maxWidth: "82%",
      borderRadius: 18,
      padding: theme.spacing(1.25, 1.5),
      boxShadow: isDarkMode
        ? "0 12px 26px rgba(2, 6, 23, 0.30)"
        : "0 10px 24px rgba(15, 23, 42, 0.08)",
      border: `1px solid ${isDarkMode ? "rgba(148, 163, 184, 0.14)" : "rgba(15, 23, 42, 0.06)"}`,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    incomingBubble: {
      background: isDarkMode ? "rgba(30,41,59,0.92)" : "#ffffff",
    },
    outgoingBubble: {
      background: isDarkMode
        ? "linear-gradient(135deg, rgba(37,99,235,0.30) 0%, rgba(14,165,233,0.24) 100%)"
        : "linear-gradient(135deg, rgba(219,234,254,1) 0%, rgba(224,242,254,1) 100%)",
    },
    messageBody: {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.6,
    },
    messageDate: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 600,
    },
    closeButton: {
      borderRadius: 999,
      paddingLeft: theme.spacing(2.25),
      paddingRight: theme.spacing(2.25),
      fontWeight: 700,
      boxShadow: "none",
    },
  };
});

const truncate = (value, size) => {
  if (!value) return "Sem mensagens recentes";
  return value.length > size ? `${value.substring(0, size).trim()}...` : value;
};

const formatUpdatedAt = (dateValue) => {
  if (!dateValue) return "Agora";

  try {
    const parsed = parseISO(dateValue);

    return isSameDay(parsed, new Date())
      ? format(parsed, "HH:mm")
      : format(parsed, "dd/MM/yyyy");
  } catch (error) {
    return "Agora";
  }
};

const DashboardManage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [openMessageModal, setOpenMessageModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const companyId = user.companyId;

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/usersMoments");
      setTickets(data || []);
    } catch (err) {
      if (err.response?.status !== 500) {
        toastError(err);
      } else {
        toast.error(`${i18n.t("frontEndErrors.getUsers")}`);
      }
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    const onAppMessage = (data) => {
      if (
        data.action === "create" ||
        data.action === "update" ||
        data.action === "delete"
      ) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [companyId, fetchTickets, socket]);

  const handleDeleteTicket = async (ticketId) => {
    const confirmDelete = window.confirm(
      "Deseja realmente fechar/excluir este atendimento?"
    );

    if (!confirmDelete) {
      return;
    }

    try {
      await api.delete(`/tickets/${ticketId}`);
      setTickets((prevTickets) =>
        prevTickets.filter((ticket) => ticket.id !== ticketId)
      );
      toast.success("Atendimento fechado com sucesso!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMessageModal = async (ticket) => {
    try {
      setLoadingMessages(true);
      setSelectedTicket(ticket);
      const { data } = await api.get(`/messages/${ticket.id}`);
      setMessages(data.messages || data || []);
      setOpenMessageModal(true);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCloseMessageModal = () => {
    setOpenMessageModal(false);
    setSelectedTicket(null);
    setMessages([]);
  };

  const { groupedMoments, pendingTickets, totalTickets, totalUnread, activeUsers } =
    useMemo(() => {
      const allTickets = Array.isArray(tickets) ? tickets : [];
      const grouped = allTickets.reduce((acc, ticket) => {
        if (!ticket.user) {
          return acc;
        }

        const userIndex = acc.findIndex((group) => group.user.id === ticket.user.id);

        if (userIndex === -1) {
          acc.push({
            user: ticket.user,
            userTickets: [ticket],
          });
        } else {
          acc[userIndex].userTickets.push(ticket);
        }

        return acc;
      }, []);

      return {
        groupedMoments: grouped,
        pendingTickets: allTickets.filter((ticket) => !ticket.user),
        totalTickets: allTickets.length,
        totalUnread: allTickets.reduce(
          (sum, ticket) => sum + Number(ticket.unreadMessages || 0),
          0
        ),
        activeUsers: grouped.length,
      };
    }, [tickets]);

  const summaryCards = [
    {
      label: "Atendimentos monitorados",
      value: totalTickets,
      caption: "Visão consolidada das conversas ativas em tempo real.",
      icon: <ForumOutlined fontSize="small" />,
    },
    {
      label: "Agentes em atuação",
      value: activeUsers,
      caption: "Colunas por atendente com distribuição visual mais clara.",
      icon: <PeopleAltOutlined fontSize="small" />,
    },
    {
      label: "Pendências",
      value: pendingTickets.length,
      caption: "Conversas sem responsável destacado para ação rápida.",
      icon: <PendingActionsOutlined fontSize="small" />,
    },
    {
      label: "Não lidas",
      value: totalUnread,
      caption: "Mensagens aguardando atenção destacadas com prioridade.",
      icon: <MarkUnreadChatAltOutlined fontSize="small" />,
    },
  ];

  const renderTicketCard = (ticket) => {
    const queueLabel = ticket.queue?.name
      ? ticket.queue.name.toUpperCase()
      : `${i18n.t("momentsUser.noqueue")}`;
    const ticketName = ticket?.contact?.name || "Contato sem nome";
    const showPreviewAction =
      user.profile === "admin" ||
      user.showDashboard === "enabled" ||
      ticket.userId === user.id;

    return (
      <Paper
        key={ticket.id}
        className={classes.ticketCard}
        elevation={0}
        onClick={() => history.push(`/tickets/${ticket.uuid}`)}
      >
        <div className={classes.ticketTopRow}>
          <div style={{ display: "flex", gap: 12, minWidth: 0, flex: 1 }}>
            <ListItemAvatar style={{ minWidth: 44, marginTop: 2 }}>
              <Avatar
                alt={ticket?.contact?.urlPicture || ticketName}
                src={ticket?.contact?.urlPicture || undefined}
                className={classes.contactAvatar}
              >
                {ticketName.charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>

            <div className={classes.ticketMain}>
              <Typography variant="subtitle1" className={classes.contactName}>
                {truncate(ticketName, 36)}
              </Typography>

              <Typography variant="body2" className={classes.ticketSnippet}>
                {truncate(String(ticket.lastMessage || ""), 90)}
              </Typography>
            </div>
          </div>

          <div className={classes.actionRow}>
            {showPreviewAction && (
              <Tooltip title="Visualizar conversa">
                <VisibilityOutlined
                  fontSize="small"
                  className={classes.actionIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMessageModal(ticket);
                  }}
                />
              </Tooltip>
            )}

            {user.profile === "admin" && (
              <Tooltip title="Fechar ou excluir atendimento">
                <DeleteOutline
                  fontSize="small"
                  className={`${classes.actionIcon} ${classes.deleteIcon}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteTicket(ticket.id);
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>

        <div className={classes.badgeRow}>
          <span className={`${classes.badge} ${classes.channelBadge}`}>
            {ticket?.whatsapp?.name || "Canal"}
          </span>

          <span
            className={classes.badge}
            style={{
              background: ticket.queue?.color || "linear-gradient(135deg, #64748b 0%, #475569 100%)",
            }}
          >
            {queueLabel}
          </span>
        </div>

        <div className={classes.footerRow}>
          <span
            className={`${classes.timeWrap} ${
              Number(ticket.unreadMessages) > 0 ? classes.unreadTime : ""
            }`}
          >
            <AccessTimeOutlined style={{ fontSize: 15 }} />
            {formatUpdatedAt(ticket.updatedAt)}
          </span>

          {Number(ticket.unreadMessages) > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`${ticket.unreadMessages} não lidas`}
              className={classes.unreadChip}
            />
          )}
        </div>
      </Paper>
    );
  };

  const renderColumn = ({ key, title, subtitle, ticketsList, avatarSrc, avatarText, pending }) => (
    <Grid item xs={12} md={6} xl={4} key={key} className={classes.columnWrap}>
      <Paper className={classes.column} elevation={0}>
        <div className={classes.columnHeader}>
          <div className={classes.columnTitleWrap}>
            <Avatar
              src={avatarSrc || undefined}
              className={`${classes.columnAvatar} ${pending ? classes.pendingAvatar : ""}`}
            >
              {avatarText}
            </Avatar>

            <div style={{ minWidth: 0 }}>
              <Typography variant="h6" className={classes.columnTitle}>
                {title}
              </Typography>
              <Typography className={classes.columnSubtitle}>{subtitle}</Typography>
            </div>
          </div>

          <Chip
            size="small"
            label={`${ticketsList.length} atendimento${ticketsList.length === 1 ? "" : "s"}`}
            className={classes.countChip}
          />
        </div>

        {ticketsList.length > 0 ? (
          <div className={classes.ticketList}>{ticketsList.map(renderTicketCard)}</div>
        ) : (
          <div className={classes.emptyState}>
            {pending ? (
              <ReportProblem className={classes.emptyStateIcon} />
            ) : (
              <ForumOutlined className={classes.emptyStateIcon} />
            )}
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              Nada por aqui agora
            </Typography>
            <Typography variant="body2">
              Novas conversas aparecerão automaticamente neste painel.
            </Typography>
          </div>
        )}
      </Paper>
    </Grid>
  );

  return (
    <div className={classes.root}>
      <Grid container spacing={2} className={classes.summaryGrid}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} xl={3} key={card.label}>
            <Paper className={classes.summaryCard} elevation={0}>
              <div className={classes.summaryLabel}>
                {card.icon}
                <span>{card.label}</span>
              </div>

              <Typography variant="h3" className={classes.summaryValue}>
                {card.value}
              </Typography>

              <Typography className={classes.summaryCaption}>{card.caption}</Typography>
              <div className={classes.summaryGlow} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} className={classes.board}>
        {renderColumn({
          key: "pending",
          title: `${i18n.t("momentsUser.pending")}`,
          subtitle: "Conversas aguardando distribuição ou primeiro atendimento.",
          ticketsList: pendingTickets,
          avatarText: "!",
          pending: true,
        })}

        {groupedMoments.map((group) =>
          renderColumn({
            key: group.user.id,
            title: group?.user?.name || `${i18n.t("momentsUser.pending")}`,
            subtitle: `${i18n.t("momentsUser.services")} ${group.userTickets.length}`,
            ticketsList: group.userTickets,
            avatarSrc: group.user.profileImage
              ? `${backendUrl}/public/company${companyId}/user/${group.user.profileImage}`
              : undefined,
            avatarText: (group?.user?.name || "U").charAt(0).toUpperCase(),
          })
        )}
      </Grid>

      <Dialog
        open={openMessageModal}
        onClose={handleCloseMessageModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" style={{ fontWeight: 800 }}>
            Conversa completa
          </Typography>
          <Typography variant="body2" className={classes.dialogSubtitle}>
            {selectedTicket?.contact?.name || "Sem contato selecionado"}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <div className={classes.messageScroll}>
            {loadingMessages ? (
              <Typography>Carregando conversa...</Typography>
            ) : messages.length > 0 ? (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={classes.messageRow}
                  style={{
                    justifyContent: message.fromMe ? "flex-end" : "flex-start",
                  }}
                >
                  <Paper
                    elevation={0}
                    className={`${classes.messageBubble} ${
                      message.fromMe ? classes.outgoingBubble : classes.incomingBubble
                    }`}
                  >
                    <Typography variant="body2" className={classes.messageBody}>
                      {message.body || "Mensagem sem conteúdo de texto."}
                    </Typography>
                    <Typography className={classes.messageDate}>
                      {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm")}
                    </Typography>
                  </Paper>
                </div>
              ))
            ) : (
              <Typography>Nenhuma mensagem encontrada.</Typography>
            )}
          </div>
        </DialogContent>

        <DialogActions style={{ padding: 20 }}>
          <Button
            onClick={handleCloseMessageModal}
            color="primary"
            variant="contained"
            className={classes.closeButton}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DashboardManage;